import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
    LayoutDashboard, MessageSquareQuote, HelpCircle, Calendar,
    Mail, Inbox, LogOut, Menu, X, ExternalLink, Wrench, Users, Package, LifeBuoy, ShoppingBag, ShoppingCart,
    FileText, Image as ImageIcon, Settings, ChevronDown, AlertCircle, Send, Bot, Wand2, KeyRound, Layers,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";

/* ------------------------------------------------------------------ */
/*  NAV STRUCTURE                                                     */
/*  • Topo: Dashboard                                                 */
/*  • Caixa de entrada (assuntos urgentes — com badge de contagem)    */
/*  • Configurações do sistema (collapsible com sub-grupos)           */
/* ------------------------------------------------------------------ */

const URGENT_ITEMS = [
    { to: "/admin/contacts", label: "Mensagens", icon: Inbox, countKey: "contacts" },
    { to: "/admin/cart-configs", label: "Pedidos do simulador", icon: Wrench, countKey: "cart_configs" },
    { to: "/admin/service-requests", label: "Pedidos de assistência", icon: LifeBuoy, countKey: "service_requests" },
    { to: "/admin/chat-leads", label: "Leads do chat", icon: Bot, countKey: "chat_leads" },
    { to: "/admin/branding-proposals", label: "Propostas de branding", icon: Wand2 },
    { to: "/admin/newsletter", label: "Subscritores newsletter", icon: Send },
];

const SETTINGS_GROUPS = [
    {
        label: "Conteúdo do site",
        items: [
            { to: "/admin/content", label: "Textos & páginas", icon: FileText },
            { to: "/admin/media", label: "Galeria de imagens", icon: ImageIcon },
            { to: "/admin/faq", label: "FAQ", icon: HelpCircle },
            { to: "/admin/testimonials", label: "Testemunhos", icon: MessageSquareQuote },
            { to: "/admin/events", label: "Eventos & marcas", icon: Calendar },
        ],
    },
    {
        label: "Catálogo & Operação",
        items: [
            { to: "/admin/products", label: "Produtos / Loja", icon: ShoppingBag },
            { to: "/admin/orders", label: "Encomendas", icon: ShoppingCart },
            { to: "/admin/equipments", label: "Carrinhos & garantias", icon: Package },
            { to: "/admin/components", label: "Catálogo de componentes", icon: Wrench },
            { to: "/admin/label-plans", label: "Planos de etiquetas", icon: Layers },
        ],
    },
    {
        label: "Sistema",
        items: [
            { to: "/admin/users", label: "Utilizadores admin", icon: Users },
            { to: "/admin/integrations", label: "Integrações / IA", icon: KeyRound },
            { to: "/admin/settings", label: "Email / SMTP", icon: Mail },
        ],
    },
];

const SETTINGS_PATHS = SETTINGS_GROUPS.flatMap((g) => g.items.map((i) => i.to));


export default function AdminLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);

    // Auto-expand "Configurações" section if current path is inside it
    const isInSettings = SETTINGS_PATHS.some((p) => location.pathname.startsWith(p));
    const [settingsOpen, setSettingsOpen] = useState(isInSettings);
    useEffect(() => {
        if (isInSettings) setSettingsOpen(true);
    }, [isInSettings]);

    // Pending counts for urgent badges (auto-refreshes on route change)
    const [counts, setCounts] = useState({ contacts: 0, cart_configs: 0, service_requests: 0 });
    const prevCounts = useRef(null);

    useEffect(() => {
        let active = true;
        const ALERT_LABELS = {
            contacts: "Nova mensagem de contacto",
            cart_configs: "Novo pedido do simulador",
            service_requests: "Novo pedido de assistência",
            chat_leads: "Novo lead do assistente IA",
        };
        function beep() {
            try {
                const ctx = new (window.AudioContext || window.webkitAudioContext)();
                [880, 1320].forEach((freq, i) => {
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.connect(gain); gain.connect(ctx.destination);
                    osc.frequency.value = freq;
                    osc.type = "sine";
                    const t = ctx.currentTime + i * 0.14;
                    gain.gain.setValueAtTime(0.0001, t);
                    gain.gain.exponentialRampToValueAtTime(0.18, t + 0.02);
                    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.28);
                    osc.start(t); osc.stop(t + 0.3);
                });
            } catch { /* audio blocked */ }
        }
        async function poll() {
            try {
                const { data } = await api.get("/admin/pending-counts");
                if (!active) return;
                const prev = prevCounts.current;
                if (prev) {
                    const increased = Object.keys(ALERT_LABELS).filter((k) => (data[k] ?? 0) > (prev[k] ?? 0));
                    if (increased.length > 0) {
                        beep();
                        increased.forEach((k) =>
                            toast.info(ALERT_LABELS[k], {
                                description: "Acabou de chegar — clica no menu lateral para ver.",
                                duration: 8000,
                            })
                        );
                    }
                }
                prevCounts.current = data;
                setCounts(data);
            } catch { /* silent — badges are decorative */ }
        }
        poll();
        const interval = setInterval(poll, 25000);
        return () => { active = false; clearInterval(interval); };
    }, []);

    function onLogout() {
        logout();
        navigate("/admin/login");
    }

    return (
        <div className="min-h-screen bg-muted text-primary flex" data-testid="admin-shell">
            {/* Sidebar */}
            <aside className={cn(
                "fixed inset-y-0 left-0 z-40 w-72 bg-primary text-white flex flex-col transition-transform lg:translate-x-0",
                open ? "translate-x-0" : "-translate-x-full"
            )}>
                <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
                    <Logo testId="admin-sidebar-logo" size="sm" variant="white" />
                    <button className="lg:hidden text-white/70" onClick={() => setOpen(false)} aria-label="Fechar">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <nav className="flex-1 px-3 py-5 space-y-6 overflow-y-auto" data-testid="admin-nav">
                    {/* Dashboard */}
                    <div>
                        <SidebarLink
                            to="/admin"
                            label="Dashboard"
                            icon={LayoutDashboard}
                            exact
                            onClick={() => setOpen(false)}
                        />
                    </div>

                    {/* Caixa de entrada (urgentes) */}
                    <div>
                        <SidebarLabel>
                            <AlertCircle className="h-3 w-3 text-secondary" />
                            Caixa de entrada
                        </SidebarLabel>
                        <div className="mt-2 space-y-0.5">
                            {URGENT_ITEMS.map((item) => (
                                <SidebarLink
                                    key={item.to}
                                    to={item.to}
                                    label={item.label}
                                    icon={item.icon}
                                    badge={item.countKey ? counts[item.countKey] : 0}
                                    onClick={() => setOpen(false)}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Configurações do sistema */}
                    <div>
                        <button
                            type="button"
                            onClick={() => setSettingsOpen((v) => !v)}
                            data-testid="admin-nav-settings-group"
                            className={cn(
                                "w-full flex items-center justify-between gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-white/85 hover:bg-white/10 hover:text-white transition",
                                settingsOpen && "text-white"
                            )}
                        >
                            <span className="inline-flex items-center gap-3">
                                <Settings className="h-4 w-4" />
                                Configurações do sistema
                            </span>
                            <ChevronDown className={cn("h-4 w-4 transition-transform", settingsOpen ? "rotate-180" : "")} />
                        </button>

                        {settingsOpen && (
                            <div className="mt-2 ml-2 pl-3 border-l border-white/10 space-y-4">
                                {SETTINGS_GROUPS.map((group) => (
                                    <div key={group.label}>
                                        <div className="px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold text-white/40">
                                            {group.label}
                                        </div>
                                        <div className="space-y-0.5">
                                            {group.items.map((item) => (
                                                <SidebarLink
                                                    key={item.to}
                                                    to={item.to}
                                                    label={item.label}
                                                    icon={item.icon}
                                                    nested
                                                    onClick={() => setOpen(false)}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </nav>
                <div className="px-3 py-4 border-t border-white/10 space-y-2 flex-shrink-0">
                    <Link to="/" target="_blank" className="flex items-center gap-3 rounded-md px-3 py-2 text-xs font-medium text-white/70 hover:text-white hover:bg-white/10">
                        <ExternalLink className="h-3.5 w-3.5" /> Ver site público
                    </Link>
                    <div className="px-3 pt-2 text-[11px] uppercase tracking-[0.18em] font-bold text-white/40 truncate">{user?.email}</div>
                    <button onClick={onLogout} data-testid="admin-logout"
                        className="w-full flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold text-white bg-white/10 hover:bg-white/20 transition">
                        <LogOut className="h-4 w-4" /> Terminar sessão
                    </button>
                </div>
            </aside>

            {/* Mobile overlay */}
            {open && (
                <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />
            )}

            {/* Main */}
            <div className="flex-1 lg:ml-72 flex flex-col min-w-0">
                <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-border">
                    <div className="px-6 lg:px-10 py-4 flex items-center justify-between">
                        <button className="lg:hidden" onClick={() => setOpen(true)} aria-label="Menu">
                            <Menu className="h-5 w-5" />
                        </button>
                        <h1 className="font-heading text-lg font-bold">Painel CMS</h1>
                        <div className="text-xs text-muted-foreground truncate max-w-[200px]">{user?.email}</div>
                    </div>
                </header>
                <main className="flex-1 px-4 lg:px-6 py-4">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Atoms                                                             */
/* ------------------------------------------------------------------ */

function SidebarLabel({ children }) {
    return (
        <div className="px-3 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-white/55">
            {children}
        </div>
    );
}

function SidebarLink({ to, label, icon: Icon, badge = 0, exact = false, nested = false, onClick }) {
    return (
        <NavLink
            to={to}
            end={exact}
            data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
            onClick={onClick}
            className={({ isActive }) => cn(
                "flex items-center gap-3 rounded-md text-sm font-medium text-white/75 transition hover:bg-white/10 hover:text-white",
                nested ? "px-3 py-2 text-[13px]" : "px-3 py-2.5",
                isActive && "bg-secondary text-primary font-bold hover:bg-yellow-300 hover:text-primary"
            )}
        >
            <Icon className={cn(nested ? "h-3.5 w-3.5" : "h-4 w-4", "flex-shrink-0")} />
            <span className="flex-1 min-w-0 truncate">{label}</span>
            {badge > 0 && (
                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-secondary text-primary text-[10px] font-bold">
                    {badge > 99 ? "99+" : badge}
                </span>
            )}
        </NavLink>
    );
}
